// 模块级标准样式（控件大号尺寸 / 卡片 / 筛选条 / 弹窗壳 / 分页）。
// 放在这里而不是只挂在 CsPageHeader 上：常量几乎被所有页面引用，能保证任一页面进入时
// 这份 CSS 一定已加载（此前仅由 CsPageHeader 携带，入口配置页一度拿不到样式）。
import './styles/standard.scss'

// ==================== 零、标签样式契约（S4.4 统一展示层） ====================

export interface CsTagStyle {
  label: string
  color: string
  bg: string
  dotColor?: string
}

// ==================== 一、时间维度 ====================

export const TIME_RANGE_OPTIONS = [
  { label: '今日', value: 'TODAY' },
  { label: '近 7 天', value: 'LAST_7_DAYS' },
  { label: '近 30 天', value: 'LAST_30_DAYS' }
] as const

export type TimeRange = (typeof TIME_RANGE_OPTIONS)[number]['value']

// ==================== 二、图表配色 ====================

export const CALL_TREND_COLORS = {
  inbound: '#2e90fa',
  answered: '#00acb0',
  rate: '#ff9f0a'
}

/** IM 趋势：会话量 / 机器人接待 / 转人工 */
export const IM_TREND_COLORS = {
  session: '#0bb7ba',
  bot: '#9b4c9c',
  toHuman: '#ff9f0a'
}

export const CHART_GRID_COLOR = '#f0f2f5'
export const CHART_AXIS_COLOR = '#e6eaed'
export const CHART_AXIS_LABEL_COLOR = '#a8b0b5'
export const CHART_AXIS_LABEL_FONT_SIZE = 10

export const CHART_CATEGORY_COLORS = [
  '#00acb0',
  '#0bb7ba',
  '#ff9f0a',
  '#fe4444',
  '#9b4c9c',
  '#2e90fa'
]

// ==================== 三、工单状态（PRD v1.5 §4.1，6 态，删 REOPENED、增 TRANSFERRED_OUT） ====================

export const TICKET_STATUS_OPTIONS = [
  { label: '待处理', value: 'OPEN' },
  { label: '处理中', value: 'PROCESSING' },
  { label: '已解决', value: 'RESOLVED' },
  { label: '已升级', value: 'UPGRADED' },
  { label: '已转出', value: 'TRANSFERRED_OUT' },
  { label: '已关闭', value: 'CLOSED' }
] as const

export type TicketStatus = (typeof TICKET_STATUS_OPTIONS)[number]['value']

export const TICKET_STATUS_TAG: Record<TicketStatus, CsTagStyle> = {
  OPEN: { label: '待处理', color: '#2e90fa', bg: '#eaf4ff' },
  PROCESSING: { label: '处理中', color: '#2e90fa', bg: '#eaf4ff' },
  RESOLVED: { label: '已解决', color: '#12b76a', bg: '#eaf9f1' },
  UPGRADED: { label: '已升级', color: '#9b4c9c', bg: '#f4f0fa' },
  TRANSFERRED_OUT: { label: '已转出', color: '#9b4c9c', bg: '#f4f0fa' },
  CLOSED: { label: '已关闭', color: '#5c6670', bg: '#f0f2f4' }
}

export const TICKET_DONUT_SEGMENTS = [
  { label: '待处理', value: 'OPEN', color: '#2e90fa' },
  { label: '处理中', value: 'PROCESSING', color: '#12b76a' },
  { label: '已解决', value: 'RESOLVED', color: '#0bb7ba' },
  { label: '已关闭', value: 'CLOSED', color: '#a8b0b5' }
] as const

// ==================== 四、工单类型 ====================

export const TICKET_TYPE_RANK = [
  { label: '咨询', value: 'SERVICE_CONSULT', color: '#00acb0' },
  { label: '售后', value: 'AFTER_SALE', color: '#0bb7ba' },
  { label: '投诉', value: 'COMPLAINT', color: '#ff9f0a' },
  { label: '退款', value: 'REFUND', color: '#fe4444' },
  { label: '计酬异议', value: 'PAYROLL_DISPUTE', color: '#9b4c9c' },
  { label: '线索类', value: 'LEAD', color: '#2e90fa' }
] as const

export type TicketType = (typeof TICKET_TYPE_RANK)[number]['value']

/** 工单类型标签色（列表页标签容器）。 */
export const TICKET_TYPE_TAG: Record<TicketType, CsTagStyle> = {
  SERVICE_CONSULT: { label: '咨询', color: '#2e90fa', bg: '#eaf4ff' },
  AFTER_SALE: { label: '售后', color: '#0bb7ba', bg: '#e6f7f6' },
  COMPLAINT: { label: '投诉', color: '#fe4444', bg: '#ffefef' },
  REFUND: { label: '退款', color: '#ff9f0a', bg: '#fff6e6' },
  PAYROLL_DISPUTE: { label: '计酬异议', color: '#9b4c9c', bg: '#f4f0fa' },
  LEAD: { label: '线索类', color: '#9b4c9c', bg: '#f3ebf5' }
}

// ==================== 五、工单优先级 ====================

export const TICKET_PRIORITY_OPTIONS = [
  { label: '低', value: 'LOW' },
  { label: '中', value: 'MIDDLE' },
  { label: '高', value: 'HIGH' },
  { label: '紧急', value: 'URGENT' }
] as const

export type TicketPriority = (typeof TICKET_PRIORITY_OPTIONS)[number]['value']

export const TICKET_PRIORITY_TAG: Record<TicketPriority, CsTagStyle> = {
  LOW: { label: '低', color: '#8a969c', bg: '#f0f2f4' },
  MIDDLE: { label: '中', color: '#8a6420', bg: '#fff6e6' },
  HIGH: { label: '高', color: '#fe4444', bg: '#fef2f2' },
  URGENT: { label: '紧急', color: '#c93a3a', bg: '#fdecec' }
}

// ==================== 六、来源与渠道 ====================

export const TICKET_SOURCE_OPTIONS = [
  { label: '电话', value: 'PHONE' },
  { label: 'IM', value: 'IM' },
  { label: '自助', value: 'SELF' },
  { label: '系统', value: 'SYSTEM' },
  { label: '活动', value: 'CAMPAIGN' },
  { label: '回访', value: 'CALLBACK' }
] as const

export const TICKET_SOURCE_TAG: Record<string, CsTagStyle> = {
  PHONE: { label: '电话', color: '#00acb0', bg: '#e6f6f6' },
  IM: { label: 'IM', color: '#2e90fa', bg: '#eaf2ff' },
  SELF: { label: '自助', color: '#5c6670', bg: '#f0f2f4' },
  SYSTEM: { label: '系统', color: '#5c6670', bg: '#f0f2f4' },
  CAMPAIGN: { label: '活动', color: '#ff9f0a', bg: '#fff6e6' },
  CALLBACK: { label: '回访', color: '#0bb7ba', bg: '#e6f7f7' }
}

/** IM / 满意度会话来源 */
export const SESSION_SOURCE_OPTIONS = [
  { label: '机器人接待', value: 'BOT' },
  { label: '人工接待', value: 'HUMAN' },
  { label: '转人工', value: 'BOT_THEN_HUMAN' }
] as const

/** 通话方向 */
export const CALL_DIRECTION_OPTIONS = [
  { label: '呼入', value: 'IN' },
  { label: '呼出', value: 'OUT' },
  { label: '回拨', value: 'CALLBACK' }
] as const

export const CALL_DIRECTION_TAG: Record<string, CsTagStyle> = {
  IN: { label: '呼入', color: '#2e90fa', bg: '#eaf4ff' },
  OUT: { label: '呼出', color: '#00acb0', bg: '#e6f6f3' },
  CALLBACK: { label: '回拨', color: '#0bb7ba', bg: '#e6f7f7' }
}

export const SERVICE_CHANNEL_OPTIONS = [
  { label: '电话', value: 'PHONE' },
  { label: 'IM', value: 'IM' },
  { label: '电话 + IM', value: 'BOTH' }
] as const

export const QUEUE_LIMIT_OPTIONS = [
  { label: '正常', value: 'NORMAL', color: '#12b76a', bg: '#e6f8f0' },
  { label: '80% 预警', value: 'WARNING', color: '#ff9f0a', bg: '#fff6e6', textColor: '#8a6420' },
  { label: '达上限', value: 'EXCEEDED', color: '#fe4444', bg: '#fdecec' }
] as const

export type QueueLimitStatus = (typeof QUEUE_LIMIT_OPTIONS)[number]['value']

export const QUEUE_LIMIT_TAG: Record<QueueLimitStatus, CsTagStyle> = {
  NORMAL: { label: '正常', color: '#12b76a', bg: '#e6f8f0' },
  WARNING: { label: '80% 预警', color: '#8a6420', bg: '#fff6e6', dotColor: '#ff9f0a' },
  EXCEEDED: { label: '达上限', color: '#fe4444', bg: '#fdecec' }
}

export const AGENT_REALTIME_OPTIONS = [
  { label: '空闲', value: 'IDLE', color: '#12b76a' },
  { label: '通话中', value: 'TALKING', color: '#2e90fa' },
  { label: '置忙', value: 'BUSY', color: '#ff9f0a' },
  { label: '后处理', value: 'ACW', color: '#9b4c9c' },
  { label: '离席', value: 'AWAY', color: '#a8b0b5' }
] as const

export type AgentRealtimeStatus = (typeof AGENT_REALTIME_OPTIONS)[number]['value']

// ==================== 九、角色标签（六角色，全站统一，§2.2） ====================

export const MEMBER_ROLE_OPTIONS = [
  { label: 'C 端', value: 'C_USER', color: '#2e90fa', bg: '#e8f2ff' },
  { label: '商户', value: 'MERCHANT', color: '#12b76a', bg: '#e6f8f0' },
  { label: '团长', value: 'LEADER', color: '#9b4c9c', bg: '#f4edf4' },
  { label: '服务人员', value: 'SERVICE_STAFF', color: '#0bb7ba', bg: '#e6f7f7' },
  { label: '代理', value: 'AGENT_PROXY', color: '#ff9f0a', bg: '#fff6e6' },
  { label: '城市合伙人', value: 'CITY_PARTNER', color: '#fe4444', bg: '#fdecec' }
] as const

export type MemberRole = (typeof MEMBER_ROLE_OPTIONS)[number]['value']

export const MEMBER_ROLE_TAG: Record<MemberRole, CsTagStyle> = MEMBER_ROLE_OPTIONS.reduce(
  (acc, item) => {
    acc[item.value] = { label: item.label, color: item.color, bg: item.bg }
    return acc
  },
  {} as Record<MemberRole, CsTagStyle>
)

export const CALL_TREND_CALIBER =
  '统计口径：按小时聚合 · 接通率 = 接通量 / 来电量 · 数据自 TCCC 话务引擎同步'

export const IM_TREND_CALIBER =
  '口径：会话量含机器人接待与人工接待 · 转人工 = BOT_THEN_HUMAN 来源会话数'

export interface ChannelLightStyle {
  bg: string
  border: string
  dot: string
  text: string
}

export const CHANNEL_STATUS_OPTIONS = [
  { label: '空闲', value: 'IDLE' },
  { label: '通话中', value: 'TALKING' },
  { label: '会话中', value: 'CHATTING' },
  { label: '后处理 ACW', value: 'ACW' },
  { label: '置忙', value: 'BUSY' },
  { label: '离席', value: 'AWAY' }
] as const

export type ChannelStatus = (typeof CHANNEL_STATUS_OPTIONS)[number]['value']

export const CHANNEL_STATUS_LIGHT: Record<ChannelStatus, ChannelLightStyle> = {
  IDLE: { bg: '#f4fcf8', border: '#cdeedd', dot: '#12b76a', text: '#12b76a' },
  TALKING: { bg: '#f4f9ff', border: '#cfe2fb', dot: '#2e90fa', text: '#2e90fa' },
  CHATTING: { bg: '#f4f9ff', border: '#cfe2fb', dot: '#2e90fa', text: '#2e90fa' },
  ACW: { bg: '#f4fbfb', border: '#c7eded', dot: '#0bb7ba', text: '#0bb7ba' },
  BUSY: { bg: '#fff9f0', border: '#ffe3bc', dot: '#ff9f0a', text: '#8a6420' },
  AWAY: { bg: '#f5f7f8', border: '#e4eaec', dot: '#a8b0b5', text: '#5c6670' }
}

// ==================== 十二、后处理小结（ACW）单选组 ====================

export const IM_SESSION_RESULT_OPTIONS = [
  { label: '客服发起结束', value: 'AGENT_END' },
  { label: '客户主动结束', value: 'USER_END' },
  { label: '超时未回复', value: 'TIMEOUT' },
  { label: '异常中断', value: 'ABORTED' }
] as const

/**
 * 通话结果码：与话单 / 后端契约统一使用 NO_ANSWER（「未接通」）。
 * 不要改成对方分支的 NOT_CONNECTED，否则话单、统计与通话小结会各用一套码值。
 */
export const CALL_RESULT_OPTIONS = [
  { label: '已接通', value: 'CONNECTED' },
  { label: '未接通', value: 'NO_ANSWER' },
  { label: '拒接', value: 'REJECTED' },
  { label: '空号', value: 'EMPTY_NUMBER' },
  { label: '关机', value: 'POWER_OFF' }
] as const

export const ACW_OPTION_COLOR = {
  selectedBg: '#059669',
  selectedText: '#ffffff',
  idleBg: '#ffffff',
  idleText: '#64748b',
  border: '#e2e8f0',
  label: '#334155',
  required: '#ef4444'
} as const

export const CALL_RESULT_NEED_FOLLOW_UP: readonly string[] = [
  'NO_ANSWER',
  'REJECTED',
  'EMPTY_NUMBER',
  'POWER_OFF'
]

export interface AgentOnlineStyle {
  label: string
  /** 状态圆点色 */
  dotColor: string
  textColor: string
}

export const AGENT_ONLINE_STATUS_OPTIONS = [
  { label: '空闲', value: 'IDLE' },
  { label: '通话中', value: 'TALKING' },
  { label: '置忙', value: 'BUSY' },
  { label: '离席', value: 'AWAY' },
  { label: '离线', value: 'OFFLINE' }
] as const

export type AgentOnlineStatus = (typeof AGENT_ONLINE_STATUS_OPTIONS)[number]['value']

export const AGENT_ONLINE_STATUS_TAG: Record<AgentOnlineStatus, AgentOnlineStyle> = {
  IDLE: { label: '空闲', dotColor: '#12b76a', textColor: '#12b76a' },
  TALKING: { label: '通话中', dotColor: '#ff9f0a', textColor: '#8a6420' },
  BUSY: { label: '置忙', dotColor: '#8a6420', textColor: '#8a6420' },
  AWAY: { label: '离席', dotColor: '#8a969c', textColor: '#5c6670' },
  OFFLINE: { label: '离线', dotColor: '#c4cbd1', textColor: '#8a969c' }
}

export const AGENT_ACCOUNT_STATUS_OPTIONS = [
  { label: '启用', value: 'ENABLED' },
  { label: '已停用', value: 'DISABLED' },
  { label: '锁定', value: 'LOCKED' }
] as const

export type AgentAccountStatus = (typeof AGENT_ACCOUNT_STATUS_OPTIONS)[number]['value']

export const AGENT_ACCOUNT_STATUS_TAG: Record<AgentAccountStatus, CsTagStyle> = {
  ENABLED: { label: '启用', color: '#12b76a', bg: '#e6f8f0', dotColor: '#12b76a' },
  DISABLED: { label: '已停用', color: '#5c6670', bg: '#f5f7f8', dotColor: '#8a969c' },
  LOCKED: { label: '锁定', color: '#8a6420', bg: '#fff6e6', dotColor: '#ff9f0a' }
}

export type AgentActionTone = 'primary' | 'danger' | 'plain'

export const AGENT_ROW_ACTIONS: Record<
  AgentAccountStatus,
  { key: string; label: string; tone: AgentActionTone }[]
> = {
  ENABLED: [
    { key: 'view', label: '查看', tone: 'primary' },
    { key: 'edit', label: '编辑', tone: 'primary' },
    { key: 'disable', label: '停用', tone: 'danger' }
  ],
  DISABLED: [
    { key: 'view', label: '查看', tone: 'primary' },
    { key: 'enable', label: '启用', tone: 'primary' },
    { key: 'delete', label: '删除', tone: 'plain' }
  ],
  LOCKED: [
    { key: 'view', label: '查看', tone: 'primary' },
    { key: 'unlock', label: '解锁', tone: 'primary' }
  ]
}

export const SKILL_STATUS_OPTIONS = [
  { label: '启用', value: 'ENABLED' },
  { label: '已停用', value: 'DISABLED' }
] as const
export type SkillStatus = (typeof SKILL_STATUS_OPTIONS)[number]['value']

export const SKILL_STATUS_TAG: Record<SkillStatus, CsTagStyle> = {
  ENABLED: { label: '启用', color: '#12b76a', bg: '#e6f8f0', dotColor: '#12b76a' },
  DISABLED: { label: '已停用', color: '#5c6670', bg: '#f5f7f8', dotColor: '#8a969c' }
}

export const SKILL_ROW_ACTIONS: Record<
  SkillStatus,
  { key: string; label: string; tone: AgentActionTone }[]
> = {
  ENABLED: [
    { key: 'edit', label: '编辑', tone: 'primary' },
    { key: 'disable', label: '停用', tone: 'danger' }
  ],
  DISABLED: [
    { key: 'edit', label: '编辑', tone: 'primary' },
    { key: 'enable', label: '启用', tone: 'primary' }
  ]
}

export const SKILL_CHANNEL_OPTIONS = ['全部', '电话', 'IM', '电话 + IM（双）'] as const

export const QUEUE_TAG_STYLE = { color: '#5c6670', bg: '#f5f7f8' } as const

export const IM_SESSION_CHANNEL_OPTIONS = [
  { label: 'IM', value: 'IM' },
  { label: '在线', value: 'ONLINE' }
] as const

export type ImSessionChannel = (typeof IM_SESSION_CHANNEL_OPTIONS)[number]['value']

export const SESSION_CHANNEL_TAG: Record<ImSessionChannel, CsTagStyle> = {
  IM: { label: 'IM', color: '#00acb0', bg: '#e6f6f6' },
  ONLINE: { label: '在线', color: '#2e90fa', bg: '#eaf2ff' }
}

export const IM_SESSION_CHANNEL_FILTERS = ['全部', 'IM', '在线'] as const

export const IM_SESSION_STATUS_OPTIONS = [
  { label: '待接入', value: 'WAITING' },
  { label: '进行中', value: 'ACTIVE' },
  { label: '已结束', value: 'ENDED' },
  { label: '已转工单', value: 'TRANSFERRED' },
  { label: '已超时', value: 'TIMEOUT' },
  { label: '客户未应答', value: 'NO_ANSWER' }
] as const

export type ImSessionStatus = (typeof IM_SESSION_STATUS_OPTIONS)[number]['value']

export const IM_SESSION_STATUS_TAG: Record<ImSessionStatus, CsTagStyle> = {
  WAITING: { label: '待接入', color: '#8a6420', bg: '#fff6e6', dotColor: '#ff9f0a' },
  ACTIVE: { label: '进行中', color: '#2e90fa', bg: '#eaf2ff', dotColor: '#2e90fa' },
  ENDED: { label: '已结束', color: '#12b76a', bg: '#e6f8f0', dotColor: '#12b76a' },
  TRANSFERRED: { label: '已转工单', color: '#8a6420', bg: '#fff6e6', dotColor: '#ff9f0a' },
  TIMEOUT: { label: '已超时', color: '#fe4444', bg: '#ffefef', dotColor: '#fe4444' },
  NO_ANSWER: { label: '客户未应答', color: '#5c6670', bg: '#f5f7f8', dotColor: '#8a969c' }
}

export const IM_SESSION_ROW_ACTION: Record<ImSessionStatus, string> = {
  WAITING: '查看记录',
  ACTIVE: '实时查看',
  ENDED: '查看记录',
  TRANSFERRED: '查看记录',
  TIMEOUT: '查看记录',
  NO_ANSWER: '查看记录'
}

export const SESSION_SCORE_COLOR = {
  good: '#12b76a',
  mid: '#8a6420',
  bad: '#fe4444',
  none: '#b4bcc4'
} as const

export const SESSION_SCORE_EMPTY_TEXT = '未评价'

export const IM_SESSION_TIME_FILTERS = ['今天', '近 7 天', '近 30 天'] as const

/** 通话方向。取 §六 已有的 CALL_DIRECTION_OPTIONS，此处只补类型别名。 */
export type CallDirection = (typeof CALL_DIRECTION_OPTIONS)[number]['value']

/** 接听结果。值与 §十二 ACW 通话结果单选同一套编码，便于后续接同一份字典。 */
export type CallResult = (typeof CALL_RESULT_OPTIONS)[number]['value']

export const CALL_RESULT_TAG: Record<CallResult, CsTagStyle> = {
  CONNECTED: { label: '已接通', color: '#12b76a', bg: '#eaf9f1' },
  NO_ANSWER: { label: '未接通', color: '#8a6420', bg: '#fff6e6' },
  REJECTED: { label: '拒接', color: '#8a6420', bg: '#fff6e6' },
  EMPTY_NUMBER: { label: '空号', color: '#8a6420', bg: '#fff6e6' },
  POWER_OFF: { label: '关机', color: '#8a6420', bg: '#fff6e6' }
}

export const CALL_RECORD_ROW_ACTIONS: Record<CallResult, readonly string[]> = {
  CONNECTED: ['详情', '试听'],
  NO_ANSWER: ['详情'],
  REJECTED: ['详情'],
  EMPTY_NUMBER: ['详情'],
  POWER_OFF: ['详情']
}

export const CALL_RECORD_DIRECTION_FILTERS = [
  '全部',
  ...CALL_DIRECTION_OPTIONS.map((item) => item.label)
] as const

export const CALL_RECORD_RESULT_FILTERS = [
  '全部',
  ...CALL_RESULT_OPTIONS.map((item) => item.label)
] as const

export const CALL_RECORD_RECORDING_EMPTY_COLOR = '#8a969c'

export const TICKET_TABLE_STATUS_TAG: Record<TicketStatus, CsTagStyle> = {
  ...TICKET_STATUS_TAG,
  PROCESSING: { label: '处理中', color: '#8a6420', bg: '#fff6e6', dotColor: '#ffb020' }
}

export const TICKET_TABLE_TYPE_TAG: Record<TicketType, CsTagStyle> = {
  ...TICKET_TYPE_TAG,
  AFTER_SALE: { label: '售后', color: '#12b76a', bg: '#eaf9f1' },
  COMPLAINT: { label: '投诉', color: '#fe4444', bg: '#feecec' },
  LEAD: { label: '线索类', color: '#9b4c9c', bg: '#f4f0fa' }
}

export const TICKET_TABLE_PRIORITY_TAG: Record<TicketPriority, CsTagStyle> = {
  ...TICKET_PRIORITY_TAG,
  LOW: { label: '低', color: '#5c6670', bg: '#f0f2f4' },
  HIGH: { label: '高', color: '#fe4444', bg: '#feecec' }
}

export const TICKET_TABLE_TAG_RADIUS = 6

// ==================== 行内操作按钮取色（全模块统一口径） ====================
/**
 * 用户口径：**主题色为主，提示性按钮用黄，警示性按钮用红，不用灰系列**。
 * primary = 常规动作；warning = 提示 / 需留意（指派、转派、启用）；danger = 破坏性（停用、下线、删除）。
 */
export type CsActionTone = 'primary' | 'warning' | 'danger'

export const ACTION_TONE: Record<string, CsActionTone> = {
  // 一般动作 → 主题色
  查看: 'primary',
  详情: 'primary',
  编辑: 'primary',
  处理: 'primary',
  发布: 'primary',
  查看记录: 'primary',
  实时查看: 'primary',
  试听: 'primary',
  配置: 'primary',
  改名: 'primary',
  保存: 'primary',
  保存修改: 'primary',
  保存并继续: 'primary',
  保存并回复: 'primary',
  提交: 'primary',
  提交回访: 'primary',
  提交评价: 'primary',
  发送: 'primary',
  导出: 'primary',
  导入: 'primary',
  开始导出: 'primary',
  开始导入: 'primary',
  完成: 'primary',
  新建: 'primary',
  新增: 'primary',
  创建: 'primary',
  复制: 'primary',
  查询: 'primary',
  刷新: 'primary',
  重置: 'primary',
  // 提示类 → 黄（需留意 / 会改变状态但可逆）
  指派: 'warning',
  批量指派: 'warning',
  转派: 'warning',
  启用: 'warning',
  升级: 'warning',
  申请权限: 'warning',
  标记已回访: 'warning',
  催办: 'warning',
  // 危险类 → 红（破坏性 / 不可逆）
  停用: 'danger',
  禁用: 'danger',
  删除: 'danger',
  下线: 'danger',
  批量下线: 'danger',
  关闭: 'danger',
  关闭工单: 'danger',
  停止: 'danger',
  强制下线: 'danger',
  作废: 'danger',
  拒接: 'danger',
  结束通话: 'danger',
  结束会话: 'danger'
}

/**
 * 行内操作按钮的 EP `type`。**按钮一律走 `type` 而不是内联 `color`**：
 * 内联色会钉死颜色、把 Element Plus 链接按钮的 hover 态一起压掉；
 * 用 `type` 才有与 `views/System`（账号管理等）一致的悬停交互。
 * 三档即全局 EP 语义色：primary #00ACB0 / warning #D98A00 / danger #EF4444。
 */
export const actionType = (action: string): CsActionTone => ACTION_TONE[action] ?? 'primary'

export const TICKET_TABLE_WEAK_COLOR = '#8a969c'

export const TICKET_TABLE_TYPE_FILTERS = [
  '全部',
  ...TICKET_TYPE_RANK.map((item) => item.label)
] as const

export const TICKET_TABLE_STATUS_FILTERS = [
  '全部',
  ...TICKET_STATUS_OPTIONS.map((item) => item.label)
] as const

export const TICKET_TABLE_PRIORITY_FILTERS = [
  '全部',
  ...TICKET_PRIORITY_OPTIONS.map((item) => item.label)
] as const

export const SATISFACTION_SCORE_FILTERS = ['全部', '好评（≥4 分）', '差评（≤3 分）'] as const

export const SATISFACTION_CHANNEL_FILTERS = ['全部', '电话', 'IM'] as const

export const SATISFACTION_VISIT_FILTERS = ['全部', '待回访', '已回访'] as const

/** 评价标签标签配色（主页面四行实绘取值） */
export const SATISFACTION_TAG_STYLE: Record<string, CsTagStyle> = {
  问题已解决: { label: '问题已解决', color: '#12b76a', bg: '#eaf9f1' },
  服务态度好: { label: '服务态度好', color: '#12b76a', bg: '#eaf9f1' },
  回复太慢: { label: '回复太慢', color: '#fe4444', bg: '#feecec' },
  未能一次解决: { label: '未能一次解决', color: '#8a6420', bg: '#fff6e6' }
}

export const SATISFACTION_TAG_FILTERS = ['全部', ...Object.keys(SATISFACTION_TAG_STYLE)] as const

export const SATISFACTION_TAG_RADIUS = 6

export const SATISFACTION_RATING_COLOR = '#ffb020'

export const SATISFACTION_RATING_BAD_COLOR = '#fe4444'

export const KNOWLEDGE_CATEGORY_STATUS_TAG: Record<string, CsTagStyle> = {
  启用中: { label: '启用中', color: '#12b76a', bg: '#eaf9f1' },
  已停用: { label: '已停用', color: '#8a969c', bg: '#f0f2f4' }
}

export const KNOWLEDGE_CATEGORY_STATUS_FILTERS = ['全部', '启用中', '已停用'] as const

export const KNOWLEDGE_ITEM_STATUS_TAG: Record<string, CsTagStyle> = {
  已发布: { label: '已发布', color: '#12b76a', bg: '#eaf9f1' },
  草稿: { label: '草稿', color: '#8a6420', bg: '#fff6e6', dotColor: '#ffb020' },
  已下线: { label: '已下线', color: '#8a969c', bg: '#f0f2f4' }
}

export const KNOWLEDGE_ITEM_STATUS_FILTERS = ['全部', '已发布', '草稿', '已下线'] as const

export const KNOWLEDGE_CATEGORY_FILTERS = [
  '全部',
  '物流与配送',
  '退款与售后',
  '会员与权益',
  '商户与经营'
] as const

export const KNOWLEDGE_TYPE_FILTERS = ['全部', '话术', 'FAQ', '文档'] as const

export const KNOWLEDGE_BATCH_BAR_BG = '#f5fbfa'
export const KNOWLEDGE_BATCH_BAR_BORDER = '#d6efea'
export const KNOWLEDGE_BATCH_BAR_DIVIDER = '#d6efea'
export const KNOWLEDGE_BATCH_BAR_ICON_BG = '#e6f6f3'

export const KNOWLEDGE_CHECKBOX_SIZE = 16
export const KNOWLEDGE_CHECKBOX_RADIUS = 4
export const KNOWLEDGE_CHECKBOX_BORDER_COLOR = '#c4cbd1'

export const KNOWLEDGE_HIT_EMPTY_COLOR = '#8a969c'

// ==================== 二十、模块内静态路由 ====================

// ==================== 工单五类型元数据（新建工单 / 工单详情 两个弹窗共用） ====================

export interface TicketTypeField {
  label: string
  required?: boolean
  placeholder: string
}

export interface TicketTypeMeta {
  name: string
  /** 类型卡上的一句说明 */
  desc: string
  link?: TicketTypeField
  exclusive?: TicketTypeField[]
  exclusiveTitle: string
  sla: string
}

/**
 * 工单五类型元数据。来源：`design_text_19_*.txt` 的 S4 弹窗区（新建工单五张类型卡）与
 * S7 工单详情（五个类型变体，各含状态标签 / 底部操作 / SLA 预告 / 流转记录），逐条原文照录。
 *
 * 新建工单与工单详情画的是**同一套字段布局**（通用字段 + 用户与单据 + 专属字段区 + 描述），
 * 详情是其只读版并另加状态标签、SLA 预告与流转记录，故两者共用本表。
 */
export const TICKET_TYPE_META: TicketTypeMeta[] = [
  {
    name: '咨询',
    desc: '解答类问题',
    link: { label: '关联会话', placeholder: '请输入关联的会话编号' },
    exclusiveTitle: '咨询类无专属字段（该区隐藏）；回复区显示知识库推荐 TOP5',
    sla: 'SLA 预告：首响 1h · 处理时限 4h（咨询类）· 超时前 20% 转临期标色'
  },
  {
    name: '投诉',
    desc: '需取证与回访',
    link: { label: '关联计酬单', placeholder: '请输入关联的计酬单号' },
    exclusiveTitle: '投诉类无专属字段（该区隐藏）；关闭后按规则生成回访任务 R-xxx',
    sla: 'SLA 预告：首响 30m · 处理时限 6h（投诉类）· 超时前 20% 转临期标色'
  },
  {
    name: '售后',
    desc: '履约类问题',
    link: { label: '关联订单号', required: true, placeholder: '请输入关联的订单号' },
    exclusiveTitle:
      '售后类无专属字段（该区隐藏）；详情页订单卡置顶 + 履约记录；涉退款时显示资金动作区',
    sla: 'SLA 预告：首响 1h · 处理时限 12h（售后类）· 超时前 20% 转临期标色'
  },
  {
    name: '退款',
    desc: '含金额与订单',
    link: { label: '关联订单号', required: true, placeholder: '请输入关联的订单号' },
    exclusive: [
      { label: '退款金额（元）', required: true, placeholder: '128.00' },
      { label: '退款原因', required: true, placeholder: '质量问题' }
    ],
    exclusiveTitle: '退款类专属字段（由类型模板动态渲染）',
    sla: 'SLA 预告：首响 1h · 处理时限 8h（退款类）· 超时前 20% 转临期标色'
  },
  {
    name: '线索类',
    desc: '转招商跟进',
    exclusive: [
      { label: '线索来源', required: true, placeholder: '电话标记' },
      { label: '联系方式', required: true, placeholder: '138****5678' },
      { label: '意向区域', required: true, placeholder: '东莞 · 长安镇' },
      { label: '意向角色', required: true, placeholder: '区县代理' }
    ],
    exclusiveTitle: '线索类专属字段（由类型模板动态渲染）',
    sla: 'SLA 预告：首响 30m · 处理时限 8h（线索类）· 主操作「分配并转出」'
  }
]

export const TICKET_FUND_ACTIONS: Record<string, string[]> = {
  退款: ['一键退款', '一键赔付'],
  售后: ['一键退款'],
  投诉: ['一键赔付']
}

export const TICKET_FUND_ACTION_STATUS = '处理中'

export const TICKET_DETAIL_WIDTH = 656

export const CS_EMPTY_BADGE_BG = '#eff3f4'
export const CS_EMPTY_BADGE_GLYPH = '#b4bcc4'
export const CS_EMPTY_BADGE_SIZE = 72

export const CS_EMPTY_SURFACE_BG = '#fafbfb'
export const CS_EMPTY_SURFACE_RADIUS = 12

export const CS_EMPTY_BUTTON_HEIGHT = 36

/** 一条空态的文案与按钮。按钮文案留空表示该态无按钮。 */
export interface CsEmptyText {
  title: string
  desc?: string
  primary?: string
  /** 次按钮（白底描边） */
  secondary?: string
  /** 补充提示（部分页面用于签入/空态说明） */
  hint?: string
}

export const CS_EMPTY_TEXT: Record<
  | 'dashboard'
  | 'workbench'
  | 'todo'
  | 'agent'
  | 'skill'
  | 'imSession'
  | 'callRecord'
  | 'ticket'
  | 'satisfaction'
  | 'knowledge'
  | 'knowledgeCategory'
  | 'quickReply'
  | 'entry'
  | 'entryConfig'
  | 'agentBot',
  Record<string, CsEmptyText>
> = {
  dashboard: {
    /** 全页空态：当日内无话务 / 会话记录 */
    none: {
      title: '暂无客服数据，坐席签入后开始统计',
      desc: '当日内无话务 / 会话记录，或筛选条件过窄。可切换时间维度为「近 7 天」查看历史数据。',
      primary: '切为近 7 天',
      secondary: '刷新'
    },
    trendEmpty: { title: '该时段暂无话务数据', desc: '坐席签入后开始统计' },
    filter: { title: '暂无匹配数据', primary: '清除筛选' }
  },
  workbench: {
    notSignedIn: {
      title: '请签入开始工作',
      desc: '签入后连接腾讯并开始接待；需要短暂离开时可示忙或小休',
      primary: '签入开始工作',
      secondary: '设备自检',
      hint: '签入前可做设备自检；签入后电话与在线状态分别核验，示忙不会结束已有会话。'
    },
    queueEmpty: {
      title: '当前无进行中会话 / 来电',
      desc: '列队区为空时展示，签入后新任务自动进入并按优先级排序。'
    },
    noCustomer: {
      title: '暂无服务对象',
      desc: '右侧客户信息卡空态；来电弹屏或 IM 进线后自动带出 360 信息。'
    },
    noCall: { title: '当前没有进行中的来电', desc: '坐席空闲时可接听分配池来电' },
    /** 状态四 · ACW 后 */
    waitingCall: { title: '等待下一通来电接入', desc: '通话已结束、小结已保存，等待下一通来电接入' }
  },
  todo: {
    none: {
      title: '暂无工单',
      desc: '当前没有分配给你的工单，可点击表格右上角「新建工单」',
      primary: '新建工单',
      secondary: '刷新'
    },
    filter: {
      title: '暂无匹配工单',
      desc: '未找到符合条件的工单，试着减少筛选条件',
      primary: '清除筛选'
    },
    noneAlt: { title: '暂无待办，去工作台看看', primary: '去工作台' },
    tabEmpty: { title: '该类型暂无工单', desc: '角标显示 0；停留在该 Tab 不自动跳回「全部」' },
    loadFail: { title: '数据加载失败，请重试', primary: '重试' }
  },
  agent: {
    none: { title: '暂无坐席', desc: '先新增坐席或批量导入' },
    filter: { title: '没有符合条件的坐席', desc: '保留已选条件，可单个删除' },
    loadFail: { title: '数据加载失败', desc: '请检查网络后重试' },
    noPermission: { title: '暂无查看权限', desc: '请联系组长开通权限' }
  },
  skill: {
    none: {
      title: '暂无技能组',
      desc: '点击右上角「新建技能组」创建第一个技能组',
      primary: '新建技能组'
    },
    filter: {
      title: '暂无匹配技能组',
      desc: '当前筛选条件下无结果，清除筛选后重试',
      primary: '清除筛选'
    },
    loadFail: { title: '数据加载失败', desc: '语音服务波动，请稍后重试', primary: '重试' },
    noPermission: {
      title: '无访问权限',
      desc: '当前账号无「技能管理」查看权限，请联系管理员',
      primary: '返回上一页'
    }
  },
  imSession: {
    none: { title: '暂无会话记录', desc: '调整时间范围或等待新会话接入', primary: '前往话单查询' },
    filter: { title: '没有匹配的结果', desc: '试试调整关键词或清空筛选条件', primary: '清空筛选' },
    loadFail: { title: '加载失败', desc: '网络异常，请稍后重试', primary: '重新加载' }
  },
  callRecord: {
    filter: { title: '没有匹配的结果', desc: '试试调整关键词或清空筛选条件', primary: '清空筛选' },
    noRecording: {
      title: '该话单无录音',
      desc: '未接通 / 呼损话单不产生录音文件',
      primary: '返回话单列表'
    },
    loadFail: { title: '加载失败', desc: '网络异常，请稍后重试', primary: '重新加载' }
  },
  ticket: {
    none: { title: '暂无工单', desc: '新建工单或由会话 / 话单转单', primary: '新建工单' },
    filter: { title: '没有匹配的工单', desc: '试试放宽 SLA / 状态条件', primary: '清空筛选' },
    noAssignPermission: {
      title: '无指派权限',
      desc: '仅「客服中心 · 主管」及以上可批量指派',
      primary: '申请权限'
    },
    loadFail: { title: '加载失败', desc: '网络异常，请稍后重试', primary: '重新加载' }
  },
  satisfaction: {
    none: { title: '暂无评价记录', desc: '当有新的客户评价时会自动展示', primary: '刷新数据' },
    filter: {
      title: '没有符合条件的评价',
      desc: '试试调整筛选条件后重新查询',
      primary: '重置筛选'
    },
    loadFail: { title: '数据加载失败', desc: '网络异常或服务暂不可用', primary: '重新加载' }
  },
  knowledge: {
    none: {
      title: '暂无知识条目',
      desc: '新建或导入知识，沉淀客服标准话术',
      primary: '新建知识',
      secondary: '批量导入'
    },
    filter: {
      title: '没有符合条件的知识',
      desc: '试试调整关键词或筛选条件后重新查询',
      primary: '重置筛选'
    },
    loadFail: { title: '数据加载失败', desc: '网络异常或服务暂不可用', primary: '重新加载' }
  },
  knowledgeCategory: {
    none: { title: '暂无分类', desc: '创建第一个分类以组织知识条目', primary: '新增分类' },
    filter: {
      title: '没有符合条件的分类',
      desc: '试试调整关键词或状态后重新查询',
      primary: '重置筛选'
    },
    loadFail: { title: '数据加载失败', desc: '网络异常或服务暂不可用', primary: '重新加载' }
  },
  quickReply: {
    none: {
      title: '暂无快捷语',
      desc: '创建第一条快捷语后，客服可在工作台一键发送',
      primary: '新建快捷语'
    },
    filter: {
      title: '没有符合条件的快捷语',
      desc: '试试调整关键词或筛选条件后重新查询',
      primary: '重置筛选'
    },
    loadFail: { title: '数据加载失败', desc: '网络异常或服务暂不可用', primary: '重新加载' }
  },
  entry: {
    none: {
      title: '暂无客服入口',
      desc: '创建第一个客服入口后，可在入口配置中设置接待与分配策略',
      primary: '新建入口'
    },
    filter: {
      title: '暂无符合条件的入口',
      desc: '试试调整筛选条件后重新查询',
      primary: '重置筛选'
    },
    loadFail: { title: '数据加载失败', desc: '网络异常或服务暂不可用', primary: '重新加载' }
  },
  entryConfig: {
    none: { title: '入口不存在或已删除', desc: '请返回客服入口管理重新选择', primary: '返回列表' },
    loadFail: { title: '配置读取失败', desc: '网络异常或服务暂不可用', primary: '重新加载' }
  },
  agentBot: {
    none: { title: '暂无智能体', desc: '内置智能体由平台预置，如需新增请联系管理员', primary: '' },
    filter: {
      title: '没有符合条件的智能体',
      desc: '试试调整关键词或能力筛选后重新查询',
      primary: '重置筛选'
    },
    loadFail: { title: '读取失败', desc: '能力读取失败，可点击「重试」重新读取', primary: '重试' }
  }
}

export const CS_EMPTY_ICON = 'ri:inbox-archive-line'

/**
 * 后端菜单尚未下发客服中心，页面暂由 `src/router/modules/remaining.ts`
 * 的隐藏路由组承载；此处集中路径常量，供页内跳转引用，避免各处硬编码。
 */
export const CS_ROUTE = {
  dashboard: '/customer-service/dashboard',
  workbench: '/customer-service/workbench',
  todo: '/customer-service/todo',
  agent: '/customer-service/agent',
  skill: '/customer-service/skill',
  imSession: '/customer-service/im-session',
  callRecord: '/customer-service/call-record',
  ticket: '/customer-service/ticket',
  satisfaction: '/customer-service/satisfaction',
  knowledge: '/customer-service/knowledge',
  knowledgeCategory: '/customer-service/knowledge/category',
  quickReply: '/customer-service/reply',
  entry: '/customer-service/entry',
  entryConfig: '/customer-service/entry/config',
  agentBot: '/customer-service/agent-bot',
  agentBotConfig: '/customer-service/agent-bot/config',
  /** 电话号码（对齐线上 /cc/phone-number/index） */
  phoneNumber: '/customer-service/phone-number',
  /** 自有号码报备（对齐线上 /cc/own-number/index） */
  ownNumber: '/customer-service/own-number',
  /** 特权号码（对齐线上 /cc/carrier-privilege/index） */
  carrierPrivilege: '/customer-service/carrier-privilege',
  /** IVR 管理（对齐线上 /cc/ivr/index） */
  ivr: '/customer-service/ivr',
  /** 分机管理（对齐线上 /cc/extension/index） */
  extension: '/customer-service/extension',
  /** 外呼管理（对齐线上 /cc/outbound/index） */
  outbound: '/customer-service/outbound',
  /** 语音智能体（对齐线上 /cc/ai-agent/index） */
  aiAgent: '/customer-service/ai-agent',
  /** 许可管理（对齐线上 /cc/license/index） */
  license: '/customer-service/license'
} as const

export const FUND_DIRECT_LIMIT = 500
export const FUND_LEADER_LIMIT = 2000

export const AGENT_ROLE_OPTIONS = ['主管', '组长', '坐席'] as const

export type AgentRole = (typeof AGENT_ROLE_OPTIONS)[number]

export const AGENT_ROLE_TAG: CsTagStyle = { label: '', color: '#5c6670', bg: '#f0f2f4' }

export const AGENT_OVERFLOW_TAG_STYLE: CsTagStyle = { label: '+2', color: '#5c6670', bg: '#f0f2f4' }
export const SKILL_OVERFLOW_TAG_STYLE: CsTagStyle = { label: '+2', color: '#5c6670', bg: '#f5f7f8' }

export const CS_MULTI_VALUE_MAX = 2

export const SKILL_BUTLER_TAG = {
  label: '管家组',
  color: '#9b4c9c',
  bg: '#f3ebf5',
  icon: 'ri:vip-crown-2-line'
} as const

export const SKILL_PRESET_TAG: CsTagStyle = { label: '系统预置', color: '#5c6670', bg: '#f5f7f8' }

export const SKILL_PRESET_LOCK_ICON = 'ri:lock-line'
export const SKILL_PRESET_READONLY = {
  bg: '#f7f8fa',
  border: '#e4eaec',
  text: '#8a969c'
} as const

export const SKILL_MEMBER_AVATAR_MAX = 3

export const SKILL_MEMBER_AVATAR_TONES = [
  { color: '#00acb0', bg: '#e6f6f6' },
  { color: '#2e90fa', bg: '#eaf2ff' },
  { color: '#9b4c9c', bg: '#f3ebf5' }
] as const

export const SKILL_MEMBER_OVERFLOW_TAG: CsTagStyle = {
  label: '+N',
  color: '#5c6670',
  bg: '#f5f7f8'
}

export const SKILL_NAME_DUPLICATE_TEXT = '技能组名称已存在，请更换后重试'

export const SKILL_NAME_ERROR_STYLE = { bg: '#fff5f5', border: '#fe4444', text: '#fe4444' } as const

export const AGENT_TRAIL_HINT = '查看全号 / 重置密码 / 停用均记录操作人、时间、IP'

// ==================== 二十二、客服工作台 · 状态变体 ====================
// 供 `workbench/` 各组件在**渲染期**取值；示例数据本身不带格式化结果。

export const CHANNEL_STATUS_LAMP_TEXT: Partial<Record<ChannelStatus, string>> = {
  BUSY: '电话 + IM · 置忙'
}

export const SLA_COUNTDOWN_WARN_SECONDS = 30 * 60

export const SLA_COUNTDOWN_STYLE = {
  normal: { color: '#a6b0b5', weight: 400 },
  warn: { color: '#8a6420', weight: 500 },
  over: { color: '#fe4444', weight: 700 }
} as const

export type SlaCountdownTone = keyof typeof SLA_COUNTDOWN_STYLE

/** 由剩余秒数取 SLA 色调（null 视为正常灰）。 */
export const slaCountdownTone = (seconds?: number | null): SlaCountdownTone => {
  if (seconds === null || seconds === undefined) return 'normal'
  if (seconds < 0) return 'over'
  return seconds <= SLA_COUNTDOWN_WARN_SECONDS ? 'warn' : 'normal'
}

export const SATISFACTION_KEY_VALUES = [1, 2, 3, 4, 5] as const

export const SATISFACTION_KEY_TEXT = {
  label: '满意度评价',
  waiting: '等待用户评价',
  /** 已评价态前缀 */
  scored: '客户已评价'
} as const

export const ACW_OUTCOME_OPTIONS = [
  { label: '已解决', value: 'RESOLVED' },
  { label: '未解决', value: 'UNRESOLVED' }
] as const

export interface CallPopupRoleVariant {
  badge: string
  fields: { label: string; value: string }[]
}

export type CallPopupRole = 'CUSTOMER' | 'MERCHANT' | 'LEADER' | 'SERVER' | 'AGENT'

/** 来电弹屏的角色徽标文案（只保留「身份名」，客户画像字段一律由客户 360 接口下发） */
export const CALL_POPUP_ROLE_BADGES: Record<string, string> = {
  CUSTOMER: 'C端用户',
  MERCHANT: '商户',
  LEADER: '团长',
  SERVER: '服务人员',
  AGENT: '代理',
  CITY_PARTNER: '城市合伙人'
}

export const RELATION_TICKET_EMPTY_COLOR = '#a6b0b5'

export const REALTIME_DISCONNECT_TEXT = {
  hint: '重连中…',
  desc: '实时面板连接已断开，自动恢复，不阻断页面'
} as const

export const TAG_OVERFLOW_LIMIT = 2
